import React, { memo, useEffect, useMemo, useState } from 'react';
import { Props } from './types';
import { Container, Title, FirstSla, SecondSla, Time, LabelSla, Border } from './styles';
import {
  CheckCircleOutlined,
  WarningOutlined,
  LoadingOutlined,
  HistoryOutlined,
  PercentageOutlined,
} from '@ant-design/icons';
import { MyuseInterval } from '../../../../../../hook/useInterval';
import { colorIconConversation, houersAndMinutes, secondsToHouers } from '../../../../Utils';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../../../store';
import { Skeleton, Tooltip } from 'antd';
import { getProgress } from './services';

const ProgressBar: React.FC<Props> = ({ protocolo, status }) => {
  const dispatch = useDispatch();
  const [nome, setNome] = useState<string>('');
  const [cor, setCor] = useState<string>('');
  const [fullTime, setFullTime] = useState<number>(0);
  const [time, setTime] = useState<number>(0);
  const [slaAtual, setSlaAtual] = useState<number>(1);
  const correndo = useSelector((state: ApplicationState) => state.ProgressBarSLA?.data['correndo']);
  const sla1 = useSelector((state: ApplicationState) => state.ProgressBarSLA?.data['sla1']);
  const sla2 = useSelector((state: ApplicationState) => state.ProgressBarSLA?.data['sla2']);
  const loading = useSelector((state: ApplicationState) => state.ProgressBarSLA?.loading);
  const severidade = useSelector(
    (state: ApplicationState) => state.ProgressBarSLA?.data['severidade']
  );

  const tempoCorrido = useSelector(
    (state: ApplicationState) => state.ProgressBarSLA?.data['tempoCorrido']
  );

  let colorIcon = colorIconConversation(severidade);
  // Nome e Cor
  useEffect(() => {
    switch (severidade) {
      case 'URGENTE':
        setNome('Urgente');
        setCor('#F5222D');
        break;
      case 'ALTA':
        setNome('Alta');
        setCor('#FA8C16');
        break;
      case 'MEDIA':
        setNome('Média');
        setCor('#52C41A');
        break;
      case 'BAIXA':
        setNome('Baixa');
        setCor('#939195');
        break;
      default:
        setNome('Sem informação');
        setCor('transparent');
        break;
    }
  }, [severidade]);

  // Definição tempo SLA
  useEffect(() => {
    setFullTime(tempoCorrido);

    if (!sla1?.definido) {
      setTime(sla1?.tempoVencer);
      setSlaAtual(1);
    } else {
      setTime(sla2?.tempoVencer);
      setSlaAtual(2);
    }
  }, [sla1, sla2, tempoCorrido, severidade, correndo, protocolo, dispatch]);

  // contador tempo SLA
  MyuseInterval(() => {
    if (correndo && time > 0) {
      setTime(time - 1);
    }
    if (status !== 'AC') {
      setFullTime(fullTime + 1);
    }
  }, 1000);

  const setProgress =
    Number(-getProgress(severidade, time, slaAtual)) + 100 > 25
      ? Number(-getProgress(severidade, time, slaAtual)) + 100
      : 25;

  const loadingIcon = useMemo(() => {
    return <LoadingOutlined style={{ color: colorIcon }} />;
  }, [colorIcon]);

  const loadingIconWhite = useMemo(() => {
    return <LoadingOutlined style={{ color: '#fff' }} />;
  }, []);

  const clockIcon = useMemo(() => {
    return <HistoryOutlined style={{ color: colorIcon }} />;
  }, [colorIcon]);

  const clockIconWhite = useMemo(() => {
    return <HistoryOutlined style={{ color: '#fff' }} />;
  }, []);

  const successIcon = useMemo(() => {
    return <CheckCircleOutlined style={{ color: colorIcon }} />;
  }, [colorIcon]);
  const successIconWhite = useMemo(() => {
    return <CheckCircleOutlined style={{ color: '#fff' }} />;
  }, []);

  const warningIcon = useMemo(() => {
    return <WarningOutlined style={{ color: colorIcon }} />;
  }, [colorIcon]);

  const warningIconWhite = useMemo(() => {
    return <WarningOutlined style={{ color: '#fff' }} />;
  }, []);

  return !loading ? (
    <main>
      <LabelSla>
        <p>
          Tempo total: <span aria-label="fullTime">{secondsToHouers(fullTime)} </span>{' '}
          {status !== 'AC' ? loadingIcon : clockIcon}
        </p>

        <div>
          <span>
            {successIcon} <p>Sucesso no SLA</p>
          </span>
          <span>
            {warningIcon} <p>Falha no SLA</p>
          </span>
        </div>
      </LabelSla>

      <Container role="progressbar">
        <Tooltip
          title={
            sla1?.definido &&
            sla2?.definido &&
            sla1?.tempoCorrido >= 0 &&
            sla2?.tempoCorrido >= 0 ? (
              <>
                Primeiro SLA: {secondsToHouers(sla1?.tempoCorrido)}
                <br />
                Segundo SLA: {secondsToHouers(sla2?.tempoCorrido)}
                <br />
              </>
            ) : (
              ''
            )
          }
          placement="bottom"
        >
          <FirstSla color={cor} data-testid="firstSla">
            <Title>{nome}</Title>
            <span className="resultSla">
              <div className="sla1">
                {sla1?.definido && sla1?.sucesso && <span>{successIconWhite}</span>}{' '}
                {sla1?.definido && !sla1?.sucesso && <span>{warningIconWhite}</span>}{' '}
              </div>
              <div className="sla2">
                {sla2?.definido && sla2?.sucesso && <span>{successIconWhite}</span>}
                {sla2?.definido && !sla2?.sucesso && <span>{warningIconWhite}</span>}
              </div>
            </span>
          </FirstSla>
        </Tooltip>

        <Border color={colorIcon}>
          <SecondSla color={cor} width={setProgress}>
            {time > 0 && (
              <Time role="timer">
                <span>- {houersAndMinutes(time)}</span>
                <span className="clock">
                  {time >= 0
                    ? correndo && !sla2.definido
                      ? loadingIconWhite
                      : clockIconWhite
                    : ''}
                </span>
              </Time>
            )}

            <span className="percentage">
              {-getProgress(severidade, time, slaAtual) + 100}{' '}
              <span>
                <PercentageOutlined />
              </span>
            </span>
          </SecondSla>
        </Border>
      </Container>
    </main>
  ) : (
    <Skeleton active loading={loading} paragraph={{ rows: 1, width: '100%' }} />
  );
};

export default memo(ProgressBar);
